import { createApi } from '@reduxjs/toolkit/query/react';

import environments from '@app/configs/environments';
import {
    deleteRequestDto,
    documentExtractResponse,
    getPdfAttributeRequestDto,
    getPdfRequestDto,
    GetProjectStatsArgs,
    paginatedDocumentResponse,
    pdfRequestDto,
    projectStatsResponse,
    restoreRequestDto,
    uuidResponse
} from '@app/store/pdf/types';
import { customBaseQueryWithReauth } from '../refresh/customBaseQuery';
import { AuthProps } from '@app/store/auth/types';
import type { RootState } from '@app/store/store';

export const PDF_PATH = 'pdf';
export const PDF_TAG = 'PDF_TAG';

export interface EditorUrlQueryType {
    organizationId: string;
    projectId: string;
    documentId: string;
    isPreview: boolean;
}

export const pdfApi = createApi({
    reducerPath: PDF_PATH,
    tagTypes: [PDF_TAG],
    refetchOnMountOrArgChange: true,
    refetchOnReconnect: true,
    refetchOnFocus: true,
    keepUnusedDataFor: 0,
    baseQuery: customBaseQueryWithReauth,
    endpoints: (builder) => ({
        createUuid: builder.mutation<uuidResponse, pdfRequestDto>({
            query: (requestBody) => ({
                url: '/pdf',
                method: 'POST',
                body: requestBody,
                params: {
                    API_KEY: environments.API_KEY
                }
            })
        }),
        getPdf: builder.query<paginatedDocumentResponse, getPdfRequestDto>({
            query: ({ organizationId, projectId, requestParams, requestBody }) => ({
                url: `/organizations/${organizationId}/projects/${projectId}/documents`,
                method: 'POST',
                params: {
                    sort_by: requestParams?.sortBy,
                    sort_order: requestParams?.sortOrder,
                    status: requestParams?.status,
                    query: requestParams?.query,
                    filter_by: requestParams?.filterBy, //for filter column
                    filter_value: requestParams?.filterValue,
                    page: requestParams?.page,
                    size: requestParams?.size
                },
                body: requestBody
            }),
            providesTags: [PDF_TAG]
        }),

        getDeletedPdf: builder.query<paginatedDocumentResponse, getPdfRequestDto>({
            query: ({ organizationId, projectId, requestParams, requestBody }) => ({
                url: `/organizations/${organizationId}/projects/${projectId}/documents/deleted`,
                method: 'POST',
                params: {
                    sort_by: requestParams?.sortBy,
                    sort_order: requestParams?.sortOrder,
                    status: requestParams?.status,
                    query: requestParams?.query,
                    filter_by: requestParams?.filterBy, // For filter column
                    filter_value: requestParams?.filterValue,
                    page: requestParams?.page,
                    size: requestParams?.size
                },
                body: requestBody
            }),
            providesTags: [PDF_TAG]
        }),

        updateDocument: builder.mutation({
            query: ({ documentId, uniqueFileName, modifiedBy }) => ({
                url: `/document/update-document`,
                method: 'PATCH',
                body: { documentId, uniqueFileName, modifiedBy }
            }),
            invalidatesTags: [PDF_TAG]
        }),

        getPdfAttributes: builder.query<Array<string>, getPdfAttributeRequestDto>({
            query: ({ organizationId, projectId, filterBy, isDeleted }) => ({
                url: `/organizations/${organizationId}/projects/${projectId}/documents/attributes`,
                method: 'GET',
                params: {
                    filter_by: filterBy,
                    isDeleted: isDeleted
                }
            })
        }),
        extractData: builder.mutation<
            { message: string },
            {
                organizationId: string;
                projectId: string;
                documentId: string;
            }
        >({
            query: (requestBody) => {
                return {
                    url: `/organizations/${requestBody.organizationId}/projects/${requestBody.projectId}/documents/${requestBody.documentId}/extract`,
                    method: 'POST'
                };
            }
        }),
        documentExtractStatus: builder.query<
            documentExtractResponse,
            {
                organizationId: string;
                projectId: string;
                documentId: string;
            }
        >({
            query: (requestBody) => {
                return {
                    url: `/organizations/${requestBody.organizationId}/projects/${requestBody.projectId}/documents/${requestBody.documentId}/extraction/status`,
                    method: 'GET'
                };
            }
        }),
        uploadPdf: builder.mutation<
            Array<uuidResponse>,
            {
                organizationId: string;
                projectId: string;
                formData: FormData;
            }
        >({
            query: (requestBody) => {
                return {
                    url: `/organizations/${requestBody.organizationId}/projects/${requestBody.projectId}/document`,
                    method: 'POST',
                    body: requestBody.formData
                };
            },
            invalidatesTags: [PDF_TAG]
        }),

        resumeDocumentProcess: builder.mutation<
            { message: string },
            {
                organizationId: string;
                projectId: string;
                documentId: string;
                formData: FormData;
            }
        >({
            query: (requestBody) => ({
                url: `/organizations/${requestBody.organizationId}/projects/${requestBody.projectId}/documents/${requestBody.documentId}/resume`,
                method: 'POST',
                body: requestBody.formData
            })
        }),

        deletePdf: builder.mutation<{ message: string }, deleteRequestDto>({
            query: ({ organizationId, projectId, documentIds }) => ({
                url: `/organizations/${organizationId}/projects/${projectId}/documents`,
                method: 'DELETE',
                body: documentIds
            }),
            invalidatesTags: [PDF_TAG]
        }),
        permanentlyDeletePdf: builder.mutation<{ message: string }, deleteRequestDto>({
            query: ({ organizationId, projectId, documentIds }) => ({
                url: `/organizations/${organizationId}/projects/${projectId}/documents/permanentlyDelete`,
                method: 'DELETE',
                body: documentIds
            }),
            async onQueryStarted({ documentIds }, { dispatch, getState, queryFulfilled }) {
                const patches = pdfApi.util
                    .selectInvalidatedBy(getState() as RootState, [PDF_TAG])
                    .filter((query) => query.endpointName === 'getDeletedPdf')
                    .map((query) =>
                        dispatch(
                            pdfApi.util.updateQueryData('getDeletedPdf', query.originalArgs as getPdfRequestDto, (draft) => {
                                const originalLength = draft.items.length;
                                draft.items = draft.items.filter((document) => !documentIds.includes(document.documentId));
                                draft.total = Math.max(0, draft.total - (originalLength - draft.items.length));
                            })
                        )
                    );

                try {
                    await queryFulfilled;
                } catch {
                    patches.forEach((patch) => patch.undo());
                }
            },
            invalidatesTags: [PDF_TAG]
        }),
        restorePdf: builder.mutation<{ message: string }, restoreRequestDto>({
            query: ({ organizationId, projectId, documentIds }) => ({
                url: `/organizations/${organizationId}/projects/${projectId}/documents/restore`,
                method: 'PUT',
                body: documentIds
            }),
            invalidatesTags: [PDF_TAG]
        }),

        getProjectStats: builder.query<projectStatsResponse, GetProjectStatsArgs>({
            query: ({ organizationId, projectIds, startDate, endDate }) => ({
                url: `/organizations/${organizationId}/projects/accounting`,
                method: 'GET',
                params: {
                    projectIds,
                    startDate,
                    endDate
                }
            })
        }),

        annotatePdf: builder.mutation<any, any>({
            query: ({ organizationId, projectId, documentId, flow, requestBody }) => ({
                url: `/organizations/${organizationId}/projects/${projectId}/documents/${documentId}/annotate`,
                method: 'POST',
                params: {
                    flow: flow
                },
                body: requestBody
            })
        }),
        anonymizePdf: builder.mutation<any, any>({
            query: ({ organizationId, projectId, documentId }) => ({
                url: `/organizations/${organizationId}/projects/${projectId}/documents/${documentId}/anonymize`,
                method: 'POST'
            })
        }),

        retryFailedProcess: builder.mutation<any, any>({
            query: ({ organizationId, projectId, documentId }) => ({
                url: `/organizations/${organizationId}/projects/${projectId}/documents/${documentId}/retry`,
                method: 'POST'
            })
        }),
        getEditorUrl: builder.query<any, EditorUrlQueryType>({
            query: (requestBody) => ({
                url: `/organizations/${requestBody.organizationId}/projects/${requestBody.projectId}/documents/${requestBody.documentId}/url`,
                params: {
                    isPreview: requestBody.isPreview
                }
            })
        }),
        getUserDetails: builder.query<AuthProps, void>({
            query: () => '/user/profile'
        })
    })
});

export const {
    useCreateUuidMutation,
    useExtractDataMutation,
    useLazyGetPdfAttributesQuery,
    useGetPdfQuery,
    useGetDeletedPdfQuery,
    usePermanentlyDeletePdfMutation,
    useUploadPdfMutation,
    useResumeDocumentProcessMutation,
    useDeletePdfMutation,
    useRestorePdfMutation,
    useAnnotatePdfMutation,
    useAnonymizePdfMutation,
    useRetryFailedProcessMutation,
    useLazyGetEditorUrlQuery,
    useDocumentExtractStatusQuery,
    useUpdateDocumentMutation,
    useGetProjectStatsQuery,
    useGetUserDetailsQuery
} = pdfApi;
